import ReviewerSummary from "@/appComponents/Blocks/Modals/PlanApprovalStatusModal/ReviewerSummary";
import { TruncatedText } from "@/appComponents/Blocks/Utils/TruncatedText/TruncatedText";
import { useSubmissionVisibility } from "@/hooks/useSubmissionVisibility";
import { PlanSubmission, ReviewerStatus } from "@/Utils/Plan/PlanSubmisionsTypes";
import {
  getSubmissionPillColors,
  getSubmissionsIcon,
  getSubmissionStatusDisplayName
} from "@/Utils/Plan/PlanSubmissionsStatuses";
import { appstr } from "@coreapp/utils/appstr";
import { FieldText, Flex, Icon, Pill, Text } from "@theorgchartdev/storybook";
import ActivityApproveRejectButtons from "../ActivityApproveRejectButtons";
import { ReviewMode, usePlanManagerContext } from "@/providers/PlanManagerProvider";
import { getPlanPermissions } from "@/Utils/getPlanPermissions";

interface SubmissionSummaryProps {
  submission: PlanSubmission;
  title: string;
}

export const SubmissionSummary: React.FC<SubmissionSummaryProps> = ({ submission, title }) => {
  const { isReviewer, hasActiveSubmission, getCurrentUserReviewStatus } =
    useSubmissionVisibility(submission);
  const { canReviewPlans } = getPlanPermissions({ useCurrentPlan: true });

  const {
    planReviewContextValue: { openReviewModal }
  } = usePlanManagerContext();

  const handleApprove = () => {
    openReviewModal(ReviewMode.APPROVE);
  };

  const handleReject = () => {
    openReviewModal(ReviewMode.REJECT);
  };

  const updatedDateString = new Date(submission.updatedAt + "Z").toLocaleString();
  const submissionDateString = new Date(submission.submission.date + "Z").toLocaleString();
  const withdrawalDateString =
    submission.withdrawn && submission.withdrawn?.date
      ? new Date(submission.withdrawn.date + "Z").toLocaleString()
      : "";

  return (
    <div className="ocn-submission-summary">
      <Flex
        padding="8px"
        justifyContent="space-between"
        alignItems="center"
        background={"var(--bg-brand-secondary-hover)"}
      >
        <Flex direction="column" gap="4px">
          <Text modifier="bold">{title}</Text>
          <Text modifier="italic" colorModifier="grey" size="small">
            {updatedDateString}
          </Text>
        </Flex>
        <Pill
          className="ocn-submission-summary-status-pill"
          label={getSubmissionStatusDisplayName(submission.status)}
          color={getSubmissionPillColors(submission.status)}
          leftContent={<Icon {...getSubmissionsIcon(submission.status)} size="small" />}
        />
      </Flex>
      <Flex direction="column" gap="16px" padding="8px">
        {submission.withdrawn ? (
          <>
            <FieldText
              field={appstr("Withdrawn by")}
              value={submission.withdrawn?.displayName || submission.withdrawn?.username || ""}
            />
            <FieldText field={appstr("Withdrawn on")} value={withdrawalDateString} />
            {submission.withdrawn?.message && (
              <div>
                <Text className="ocn-field-text-field">{appstr("Withdrawal message")}</Text>
                <TruncatedText text={submission.withdrawn.message} maxLines={4} />
              </div>
            )}
          </>
        ) : (
          <>
            <FieldText
              field={appstr("Submitted by")}
              value={submission.submission.displayName || submission.submission.username}
            />
            <FieldText field={appstr("Submitted on")} value={submissionDateString} />
            {submission.submission.message && (
              <div>
                <Text className="ocn-field-text-field">{appstr("Submission message")}</Text>
                <TruncatedText text={submission.submission.message} maxLines={4} />
              </div>
            )}
          </>
        )}
        <Flex direction="column" gap="8px">
          <Text className="ocn-field-text-field">{appstr("Approvers label")}</Text>
          {submission.reviewers.map(reviewer => (
            <ReviewerSummary key={reviewer.username} reviewer={reviewer} />
          ))}
        </Flex>
      </Flex>
      {canReviewPlans && isReviewer && hasActiveSubmission && (
        <div className="ocn-submission-summary-footer">
          <ActivityApproveRejectButtons
            reviewerStatus={getCurrentUserReviewStatus() as ReviewerStatus}
            onApprove={handleApprove}
            onReject={handleReject}
            dataTestIdPrefix="approvals-tab"
          />
        </div>
      )}
    </div>
  );
};
